import axios, { AxiosResponse } from "axios";
import { ArrayFromEnvelope, configService, ObjectFromEnvelope } from "./config.service";
import { Duty } from "../models/duties";
import { convertDateOnlyToUtc } from "../utils/dateUtils";
import { getQueryClient, PERIOD_STALE_TIME, invalidatePeriod } from "../queryClient";

class DutiesService {
  getConflictsOnDuties(from: Date, to: Date, employeeId: number | null = null): Promise<any[]> {
    const startUtc = convertDateOnlyToUtc(from);
    const endUtc = convertDateOnlyToUtc(to);
    const params: any = {
      start_date: startUtc,
      end_date: endUtc
    };
    // The endpoint is scoped by employee OR institution depending on the caller;
    // the queryKey must encode that scope so an employee_id can't collide with an
    // institution_id of equal numeric value.
    const scope = employeeId ? "employee" : "institution";
    const scopeId = employeeId ?? configService.institutionId();
    if (employeeId) {
      params.employee_id = employeeId;
    } else {
      params.institution_id = configService.institutionId();
    }
    return getQueryClient().fetchQuery({
      queryKey: ["conflicts", scope, scopeId, startUtc, endUtc],
      queryFn: async () => {
        const response = await axios.get<any>(
          configService.personaleOrAdminUrl("duties/get_conflicts_on_duties.json"),
          { params }
        );
        return response.data.data as any[];
      },
      staleTime: PERIOD_STALE_TIME
    });
  }

  async fetchDuties(fra: Date, tilOgMed: Date): Promise<Duty[]> {
    const utcFra: string = convertDateOnlyToUtc(fra);
    const utcTilOgMed: string = convertDateOnlyToUtc(tilOgMed);
    const institutionId = configService.institutionId();

    // Cache the RAW enveloped JSON and parse per call — Duty.fromJSONArray returns
    // freezable instances that the duties store Object.freeze()s in place, so the
    // cache must never hold parsed (and later frozen) objects.
    const raw = await getQueryClient().fetchQuery({
      queryKey: ["duties", institutionId, utcFra, utcTilOgMed],
      queryFn: async () => {
        const response = await axios.get<Duty[]>(
          configService.personaleOrAdminUrl("duties.json"),
          {
            params: {
              institution_id: institutionId,
              end_date: utcTilOgMed,
              start_date: utcFra
            }
          }
        );
        return ArrayFromEnvelope<Duty>(response.data);
      },
      staleTime: PERIOD_STALE_TIME
    });
    return Duty.fromJSONArray(raw);
  }
  async fetchDutiesByEmployeeId(
    employeeId: number,
    startDate: Date,
    endDate: Date
  ): Promise<Duty[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Duty[]>(configService.APIInstitutionUrl("duties.json"), {
          params: {
            employee_id: employeeId,
            start_date: convertDateOnlyToUtc(startDate),
            end_date: convertDateOnlyToUtc(endDate)
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Duty.fromJSONArray(ArrayFromEnvelope<Duty>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async fetchDutiesByEventId(eventId: number): Promise<Duty[]> {
    return new Promise((resolve, reject) => {
      axios
        .get<Duty[]>(configService.APIInstitutionUrl("duties.json"), {
          params: {
            event_id: eventId
          }
        })
        .then((response: AxiosResponse) => {
          resolve(Duty.fromJSONArray(ArrayFromEnvelope<Duty>(response.data)));
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  async createDuty(duty: Duty): Promise<Duty> {
    return new Promise((resolve, reject) => {
      axios
        .post<Duty>(configService.APIInstitutionUrl("duties.json"), duty.convertToPostObject())
        .then((response: AxiosResponse) => {
          invalidatePeriod("duties", "conflicts", "webAcceptPages");
          resolve(Duty.fromJSON(ObjectFromEnvelope<Duty>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async updateDuty(duty: Duty): Promise<Duty> {
    return new Promise((resolve, reject) => {
      axios
        .put<Duty>(
          configService.APIInstitutionUrl("duties/" + duty.id + ".json"),
          duty.convertToPutObject()
        )
        .then((response: AxiosResponse) => {
          invalidatePeriod("duties", "conflicts", "webAcceptPages");
          resolve(Duty.fromJSON(ObjectFromEnvelope<Duty>(response.data)));
        })
        .catch((error) => reject(error));
    });
  }
  async deleteDuty(dutyId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete<Duty>(configService.APIInstitutionUrl("duties/" + dutyId + ".json"))
        .then((response: AxiosResponse) => {
          invalidatePeriod("duties", "conflicts", "webAcceptPages");
          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  }
}

export const dutiesService: DutiesService = new DutiesService();
